import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCart, saveCart } from '../api';
import { useAuth } from '../hooks/useAuth';
import { usePersistedState } from '../hooks/usePersistedState';
import {
  TourPurchaseGuestsInfo,
  TourPurchaseNumberOfGuests,
} from '../pages/TourPurchase';

export type CartItem = TourPurchaseNumberOfGuests & {
  id: string;
  guests: TourPurchaseGuestsInfo;
};

export type Cart = {
  tourId?: number;
  items: CartItem[];
};

export type CartContextType = {
  cart: Cart;
  addToCart: (tourId: number, value: Omit<CartItem, 'id'>) => Promise<string>;
  removeFromCart: (id: string) => Promise<void>;
  resetCart: () => void;
};

const initialCartState: CartContextType = {
  cart: { items: [] },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  addToCart: () => Promise.resolve(''),
  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  removeFromCart: async (id: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetCart: () => {},
};

export const CartContext = createContext<CartContextType>(initialCartState);

export default function CartProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { token } = useAuth();
  const [cart, setCart, resetCart] = usePersistedState<Cart>(
    { items: [] },
    'cart',
  );
  const [skipSave, setSkipSave] = useState<boolean>(true);
  const { t } = useTranslation();

  // save cart with api
  useEffect(() => {
    if (!token) return;
    if (skipSave) {
      setSkipSave(false);
      return;
    }
    saveCart(token, JSON.stringify(cart));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, token]);

  // load cart from api
  useEffect(() => {
    if (!token) return;
    // skip saving cart on first load
    setSkipSave(true);
    getCart(token).then((data) => {
      try {
        const cart = JSON.parse(data.payload) as Cart;
        // do not overwrite empty cart data
        if (cart && cart.items?.length > 0 && cart.tourId) setCart(cart);
        setTimeout(() => {
          // allow saving cart after first load
          setSkipSave(false);
        }, 400);
      } catch (e) {
        console.error('Could not parse cart data');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function addToCart(tourId: number, item: Omit<CartItem, 'id'>) {
    if (cart.tourId && cart.tourId !== tourId) {
      throw new Error(t('cartContext.multipleTourError'));
    }

    // generate id
    const id = Math.random().toString(36).slice(2, 9);

    setCart({
      tourId,
      items: [
        ...cart.items,
        {
          id,
          ...item,
        },
      ],
    });

    return Promise.resolve(id);
  }

  async function removeFromCart(id: string) {
    const items = cart.items.filter((item) => item.id !== id);
    const tourId = items.length > 0 ? cart.tourId : undefined;
    setCart({
      tourId,
      items,
    });
  }

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        resetCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
